import React, { forwardRef } from "react";
import { NOTE_NAMES_SHARP, WHITE_PCS } from "../lib/music";

/**
 * Piano SVG renderer.
 * Starts at `startingPc` (a WHITE-KEY pitch class 0..11) at `startingOctaveNumber`.
 * Renders `octaves` full octaves (7 white keys each).
 * Highlights either by pitch class (highlightedPCs) OR by specific semitone
 * offsets from the leftmost key (highlightedOffsets), used for chord inversions.
 */
const WHITE_KEY_W = 60;
const WHITE_KEY_H = 260;
const BLACK_KEY_W = 36;
const BLACK_KEY_H = 160;

function getKeyLayout(startingPc, octaves) {
  const whiteKeys = [];
  const blackKeys = [];
  let whiteIdx = 0;
  const totalSemis = octaves * 12;
  for (let semi = 0; semi < totalSemis; semi++) {
    const pc = (startingPc + semi) % 12;
    if (WHITE_PCS.includes(pc)) {
      whiteKeys.push({
        x: whiteIdx * WHITE_KEY_W,
        pc,
        semi,
      });
      whiteIdx += 1;
    } else {
      blackKeys.push({
        x: whiteIdx * WHITE_KEY_W - BLACK_KEY_W / 2,
        pc,
        semi,
      });
    }
  }
  return { whiteKeys, blackKeys, totalWhite: whiteIdx };
}

const Piano = forwardRef(function Piano(
  {
    octaves = 2,
    startingPc = 0,
    startingOctaveNumber = 4,
    highlightedPCs = new Set(),
    highlightedOffsets = null,
    playingSemis = null,
    highlightColor = "#3B82F6",
    backgroundColor = "#FFFFFF",
    showLabels = true,
    noteNames = NOTE_NAMES_SHARP,
  },
  ref
) {
  const { whiteKeys, blackKeys, totalWhite } = getKeyLayout(
    startingPc,
    octaves
  );
  const width = totalWhite * WHITE_KEY_W;
  const height = WHITE_KEY_H + 40;

  const isHighlighted = (semi, pc) => {
    if (highlightedOffsets) return highlightedOffsets.has(semi);
    return highlightedPCs.has(pc);
  };
  const isPlaying = (semi) => playingSemis && playingSemis.has(semi);
  const PLAY_COLOR = "#FFFFFF";

  const startingAbs = startingPc + 12 * startingOctaveNumber;

  const labelFor = (semi) => {
    const abs = startingAbs + semi;
    return {
      name: noteNames[((abs % 12) + 12) % 12],
      octave: Math.floor(abs / 12),
    };
  };

  return (
    <div
      className="w-full flex items-center justify-center"
      data-testid="piano-svg-container"
    >
      <svg
        ref={ref}
        xmlns="http://www.w3.org/2000/svg"
        viewBox={`-20 -20 ${width + 40} ${height + 40}`}
        width="100%"
        style={{
          maxWidth: `${width + 40}px`,
          background: backgroundColor,
          display: "block",
        }}
        aria-label="Piano keyboard visualization"
      >
        <rect
          x={-20}
          y={-20}
          width={width + 40}
          height={height + 40}
          fill={backgroundColor}
        />

        {whiteKeys.map((k) => {
          const hi = isHighlighted(k.semi, k.pc);
          const pl = isPlaying(k.semi);
          return (
            <rect
              key={`w-${k.semi}`}
              x={k.x}
              y={0}
              width={WHITE_KEY_W}
              height={WHITE_KEY_H}
              fill={pl ? PLAY_COLOR : hi ? highlightColor : "#FFFFFF"}
              stroke="#111827"
              strokeWidth={2}
            />
          );
        })}

        {blackKeys.map((k) => {
          const hi = isHighlighted(k.semi, k.pc);
          const pl = isPlaying(k.semi);
          return (
            <rect
              key={`b-${k.semi}`}
              x={k.x}
              y={0}
              width={BLACK_KEY_W}
              height={BLACK_KEY_H}
              fill={pl ? PLAY_COLOR : hi ? highlightColor : "#111827"}
              stroke="#111827"
              strokeWidth={2}
            />
          );
        })}

        {showLabels &&
          whiteKeys.map((k) => {
            const hi = isHighlighted(k.semi, k.pc);
            const { name, octave } = labelFor(k.semi);
            return (
              <text
                key={`wl-${k.semi}`}
                x={k.x + WHITE_KEY_W / 2}
                y={WHITE_KEY_H - 18}
                textAnchor="middle"
                fontFamily="'JetBrains Mono', ui-monospace, monospace"
                fontSize={16}
                fontWeight={700}
                fill={hi ? "#FFFFFF" : "#111827"}
              >
                {name}
                
              </text>
            );
          })}

        {showLabels &&
          blackKeys.map((k) => {
            const hi = isHighlighted(k.semi, k.pc);
            if (!hi) return null;
            const { name } = labelFor(k.semi);
            return (
              <text
                key={`bl-${k.semi}`}
                x={k.x + BLACK_KEY_W / 2}
                y={BLACK_KEY_H - 12}
                textAnchor="middle"
                fontFamily="'JetBrains Mono', ui-monospace, monospace"
                fontSize={12}
                fontWeight={700}
                fill="#FFFFFF"
              >
                {name}
              </text>
            );
          })}
      </svg>
    </div>
  );
});

export default Piano;
